import requestWrapper from '../../requestWrapper';
import endpoints from './endpoints';
import type {
	Album,
	AlbumPerformances,
	AlbumDemographic,
	DemographicType,
	AlbumFansEvolution,
	VerifyLabelOwnershipResponse,
	VerifyOwnershipResponse,
	AlbumDemographics,
} from './types';
import {StakeholderType, OfferType} from './types';
import type {DataResponseWrapper} from '@deezer/next/services/api/Starmania/requests/types';

const {get} = requestWrapper;

const requests = {
	verifyAlbumUpc: (
		upc: string,
		stakeholderIds: number,
		stakeholderType: StakeholderType.ARTIST | StakeholderType.LABEL,
	) => {
		return get<VerifyOwnershipResponse>(endpoints.verifyUpc(upc), {
			params: {
				stakeholderType,
				stakeholderIds,
			},
		});
	},
	verifyAlbumUpcForLabel: (
		upc: string,
		labelName: string,
		labelId: number,
		providerId: number,
	) =>
		get<VerifyLabelOwnershipResponse>(endpoints.verifyUpc(upc), {
			params: {
				stakeholderType: StakeholderType.LABEL,
				labelName,
				stakeholderIds: labelId,
				providerId,
			},
		}),
	get: (upc: string, deezerId?: number) =>
		get<DataResponseWrapper<Album>>(endpoints.albumByUPC(upc), {
			params: {
				deezerId: deezerId || undefined,
			},
		}),
	getDemographics: (
		upc: string,
		startDate: string,
		endDate: string,
		offer: OfferType,
		country: string,
	) => {
		let params: {
			startDate: string;
			endDate: string;
			offer?: string;
			country?: string;
		} = {
			startDate,
			endDate,
		};

		if (offer && offer !== OfferType.all) {
			params = {
				...params,
				offer,
			};
		}
		if (country && country !== 'WW' && country !== 'ww') {
			params = {
				...params,
				country: country.toUpperCase(),
			};
		}

		return get<DataResponseWrapper<AlbumDemographics>>(
			endpoints.demographics(upc),
			{
				params,
			},
		);
	},

	getDemographic: (
		upc: string,
		type: DemographicType,
		startDate: string,
		endDate: string,
		offer: OfferType,
		country: string,
	) => {
		let params: {
			startDate: string;
			endDate: string;
			offer?: string;
			country?: string;
		} = {
			startDate,
			endDate,
		};

		if (offer && offer !== OfferType.all) {
			params = {
				...params,
				offer,
			};
		}
		if (country && country !== 'WW' && country !== 'ww') {
			params = {
				...params,
				country: country.toUpperCase(),
			};
		}

		return get<DataResponseWrapper<AlbumDemographic>>(
			endpoints.albumDemographic(upc, type),
			{
				params,
			},
		);
	},
	getFans: (upc: string, startDate: string, endDate: string) =>
		get<DataResponseWrapper<AlbumFansEvolution | null>>(
			endpoints.albumFans(upc),
			{
				params: {
					startDate,
					endDate,
				},
			},
		),
	getPerformances: (
		upc: string,
		startDate: string,
		endDate: string,
		offer?: OfferType,
		country?: string,
	) => {
		let params: {
			startDate: string;
			endDate: string;
			offer?: string;
			country?: string;
		} = {
			startDate,
			endDate,
		};

		if (offer && offer !== OfferType.all) {
			params = {
				...params,
				offer,
			};
		}
		if (country && country !== 'WW' && country !== 'ww') {
			params = {
				...params,
				country: country.toUpperCase(),
			};
		}

		return get<DataResponseWrapper<{[key: number]: AlbumPerformances[]}>>(
			endpoints.albumPerformances(upc),
			{
				params,
			},
		);
	},
	getImportAlbums: (upc: string) => {
		return get<DataResponseWrapper<Album[]>>(endpoints.importAlbums, {
			params: {
				upc,
			},
		});
	},
};

export default requests;
